import { Button, Text } from '@mantine/core';
import { IconTrash, IconCopy, IconRefresh } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ServiceBooking } from '@turizi/shared';
import { useDeleteServiceBooking, useSaveServiceBooking, useUpdateServiceBookingServiceData } from "@/hooks/api-hooks";
import { openConfirmModal } from "@mantine/modals";

interface ServiceBookingMenuProps {
    booking: ServiceBooking;
    onActionSuccess?: () => void;
    onError?: (err: any) => void;
    editable?: boolean;
}

export default function ServiceBookingMenu({
    booking,
    onActionSuccess,
    onError,
    editable = true,
}: ServiceBookingMenuProps) {
    const { t } = useTranslation();
    const { mutate: deleteMutation, isPending: deleting } = useDeleteServiceBooking();
    const { mutate: duplicateMutation, isPending: duplicating } = useSaveServiceBooking();
    const { mutate: updateServiceDataMutation, isPending: updatingServiceData } = useUpdateServiceBookingServiceData();

    const handleDelete = (e: any) => {
        e.stopPropagation();
        openConfirmModal({
            title: t('delete'),
            children: (
                <Text size="sm">
                    {t('confirm_delete')}
                </Text>
            ),
            labels: { confirm: t('delete'), cancel: t('cancel') },
            confirmProps: { color: 'red' },
            onConfirm: () => {
                deleteMutation({ id: booking.id }, {
                    onSuccess: () => {
                        onActionSuccess?.();
                    },
                    onError: (err: any) => {
                        if (onError) onError(err);
                    }
                });
            }
        });
    };

    const handleDuplicate = (e: any) => {
        e.stopPropagation();
        const aux = {
            service_id: booking?.service_id,
            reservation_id: booking?.reservation_id,
            start_day: booking?.start_day,
            end_day: booking?.end_day,
            adults: booking?.adults,
            children: booking?.children,
            seniors: booking?.seniors,
            infants: booking?.infants,
            guests: booking?.guests,
            persons: booking?.persons,
            pax_groups: booking?.pax_groups?.map((bg) => ({
                pax_group_id: bg.pax_group_id,
                adults: bg.adults,
                children: bg.children,
                seniors: bg.seniors,
                infants: bg.infants,
                guests: bg.guests,
                pax_count: bg.pax_count
            })),
            selected_provider_id: booking?.selected_provider_id,
            booking_attributes: (booking as any)?.booking_attributes,
            discount_percentage: booking?.discount_percentage,
            discount_type: booking?.discount_type,
            discount_id: booking?.discount_id,
        };
        duplicateMutation(aux as any, {
            onSuccess: () => {
                onActionSuccess?.();
            },
            onError: (err: any) => {
                if (onError) onError(err);
            }
        });
    };

    const handleUpdateServiceData = (e: any) => {
        e.stopPropagation();
        // commented for now, it closes the modal
        updateServiceDataMutation({ id: booking.id }, {
            // onSuccess: () => {
            //     // onActionSuccess?.();
            // },
            // onError: (err: any) => {
            //     // if (onError) onError(err);
            // }
        });
    };

    if (!editable) return null;

    return (
        <div className="flex items-center gap-4 flex-wrap" onClick={(e) => e.stopPropagation()}>
            <Button
                size="xs"
                variant="light"
                color="red"
                radius="md"
                leftSection={<IconTrash size={14} />}
                onClick={handleDelete}
                loading={deleting}
                disabled={deleting || duplicating}
                className="px-2.5 sm:px-3"
            >
                {t('delete')}
            </Button>
            <Button
                size="xs"
                variant="light"
                color="gray"
                radius="md"
                leftSection={<IconCopy size={14} />}
                onClick={handleDuplicate}
                loading={duplicating}
                disabled={deleting || duplicating || updatingServiceData}
                className="px-2.5 sm:px-3"
            >
                {t('duplicate')}
            </Button>
            <Button
                size="xs"
                variant="light"
                color="gray"
                radius="md"
                leftSection={<IconRefresh size={14} />}
                onClick={handleUpdateServiceData}
                loading={updatingServiceData}
                disabled={deleting || duplicating || updatingServiceData}
                className="px-2.5 sm:px-3"
            >
                {t('update_service_data')}
            </Button>
        </div>
    );
}
